import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import Modal from 'components/Modal';
import { declineFlightTicketProposalOffer, useDeclineReasons } from 'api/Flights/sdk';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'components/Button';
import { t } from 'i18next';
import { cn } from 'utils/utils.ts';

const MAX_CHARACTERS = 2000;
const OPTION_DEFAULT_VALUE = '';

type DeclineOfferModalProps = {
  open: boolean;
  flightTicketProposalUUID: string;
  onClose: () => void;
};

const schema = yup.object().shape({
  reason: yup.string().required('Reason is required'),
  note: yup
    .string()
    .defined()
    .when(['reason'], {
      is: 'other',
      then: (schema) => schema.trim().min(4, t('flightOffer.otherReasonForDeclining')),
    }),
});

type DeclineOfferModalForm = yup.InferType<typeof schema>;

const DeclineOfferModal = ({ open, flightTicketProposalUUID, onClose }: DeclineOfferModalProps) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: declineReasons } = useDeclineReasons();

  const form = useForm({
    defaultValues: {
      reason: '',
      note: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const noteValue = form.watch('note') ?? '';
  const noteLength = noteValue.length;

  const selectedReason = form.watch('reason');
  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleConfirm = (values: DeclineOfferModalForm) => {
    declineFlightTicketProposalOffer({
      flightTicketProposalUUID,
      declineReason: values.reason,
      declineNote: values.note,
    }).finally(handleClose);
  };

  return (
    <form onSubmit={form.handleSubmit(handleConfirm)} className='flex flex-col'>
      <Modal
        open={open}
        onClose={handleClose}
        title={t('flightOffer.declineOffer')}
        subtitle={t('flightOffer.reasonForDeclining')}
        content={
          <FormProvider {...form}>
            <Controller
              name='reason'
              render={({ field }) => (
                <select
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    form.trigger();
                  }}
                  onBlur={() => form.trigger()}
                  aria-label='Decline Offer Select'
                  className={cn(
                    'border border-gray-400 rounded-sm p-2 mb-4',
                    selectedReason === OPTION_DEFAULT_VALUE && 'text-gray-400',
                    form.formState.errors.reason && 'outline-2 outline-red'
                  )}
                >
                  <option value={OPTION_DEFAULT_VALUE} disabled>
                    {t('flightOffer.chooseReasonForDeclining')}
                  </option>

                  {declineReasons?.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              )}
            ></Controller>
            <div className='flex flex-col gap-1'>
              <label htmlFor='decline-note' className='font-[MuseoSansCyrl] text-sm text-gray-600'>
                {t('flightOffer.message')}
              </label>
              <Controller
                name='note'
                render={({ field }) => (
                  <textarea
                    {...field}
                    ref={textareaRef}
                    rows={4}
                    maxLength={MAX_CHARACTERS}
                    className={`border w-full rounded-sm ${form.formState.errors.note ? 'border-2 border-red' : 'border-gray-400'}`}
                  />
                )}
              ></Controller>
            </div>
            {form.formState.errors.note && (
              <p className='text-sm font-[MuseoSansCurl] text-red'>{form.formState.errors.note.message}</p>
            )}
            <p
              className={cn(
                'font-[MuseoSansCyrl] text-sm text-gray-600 mt-2',
                noteLength === MAX_CHARACTERS && 'text-red font-[MuseoSans900]'
              )}
            >
              {t('flightOffer.charactersLeft')}: {MAX_CHARACTERS - form.watch('note').length}
            </p>
          </FormProvider>
        }
        buttons={
          <div className='w-full flex justify-end'>
            <Button type='submit' className='font-sans font-semibold' disabled={!form.formState.isValid}>
              {t('common.confirm')}
            </Button>
          </div>
        }
      />
    </form>
  );
};

export default DeclineOfferModal;
